/**
 * G2eGente - Modal de Compartilhamento de Vaga com Banner para Redes Sociais
 * Gera banners otimizados com logo da empresa, QR Code do app, e informações da vaga
 */
import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Copy, 
  Download, 
  Share2,
  ExternalLink,
  Square,
  Smartphone,
  Loader2
} from 'lucide-react';
import { companiesAPI, jobPositionsAPI } from '../lib/api';

// PWA download URL - Goes to dedicated install page
const PWA_URL = 'https://www.g2egente.com.br/app';

export default function ShareJobModal({ open, onOpenChange, job }) {
  const [format, setFormat] = useState('square'); // 'square' or 'vertical'
  const [generating, setGenerating] = useState(false);
  const [company, setCompany] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [jobPosition, setJobPosition] = useState(null); // Cargo vinculado
  
  const baseUrl = window.location.origin;
  const jobUrl = `${baseUrl}/vagas/${job?.id}`;
  
  // Fetch company data with logo and job position data
  useEffect(() => {
    if (open) {
      fetchCompanyData();
      fetchJobPositionData();
    }
  }, [open, job?.cargo_id]);

  const fetchCompanyData = async () => {
    try {
      const response = await companiesAPI.getMe();
      setCompany(response.data);
      
      // Pre-load logo image if exists
      if (response.data.logo_url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setLogoImage(img);
        img.onerror = () => setLogoImage(null);
        img.src = `${process.env.REACT_APP_BACKEND_URL}${response.data.logo_url}`;
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  // Fetch job position (Engenharia de Cargo) to get descricao_sumaria
  const fetchJobPositionData = async () => {
    if (job?.cargo_id) {
      try {
        const response = await jobPositionsAPI.getById(job.cargo_id);
        setJobPosition(response.data);
      } catch (error) {
        console.error('Error fetching job position:', error);
        setJobPosition(null);
      }
    } else {
      setJobPosition(null);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jobUrl);
      toast.success('Link copiado!');
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  // Extract dominant color from logo
  const getAccentColor = () => {
    if (!logoImage) return '#0891b2'; // Default cyan
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = logoImage.width;
      canvas.height = logoImage.height;
      ctx.drawImage(logoImage, 0, 0);
      
      // Sample colors from different parts of the image
      const samples = [];
      const samplePoints = [
        [Math.floor(logoImage.width * 0.25), Math.floor(logoImage.height * 0.25)],
        [Math.floor(logoImage.width * 0.75), Math.floor(logoImage.height * 0.25)],
        [Math.floor(logoImage.width * 0.5), Math.floor(logoImage.height * 0.5)],
      ];
      
      for (const [x, y] of samplePoints) {
        const pixel = ctx.getImageData(x, y, 1, 1).data;
        if (pixel[3] > 128 && (pixel[0] < 240 || pixel[1] < 240 || pixel[2] < 240)) {
          samples.push({ r: pixel[0], g: pixel[1], b: pixel[2] });
        }
      }
      
      if (samples.length > 0) {
        let bestColor = samples[0];
        let maxSaturation = 0;
        
        for (const c of samples) {
          const max = Math.max(c.r, c.g, c.b);
          const min = Math.min(c.r, c.g, c.b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          if (saturation > maxSaturation) {
            maxSaturation = saturation;
            bestColor = c;
          }
        }
        
        return `rgb(${bestColor.r}, ${bestColor.g}, ${bestColor.b})`;
      }
    } catch (e) {
      console.error('Error extracting color:', e);
    }
    
    return '#0891b2';
  };

  const generateBanner = async () => {
    setGenerating(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Dimensions based on format
      const isSquare = format === 'square';
      canvas.width = 1080;
      canvas.height = isSquare ? 1080 : 1920;
      
      const accentColor = getAccentColor();
      const width = canvas.width;
      const height = canvas.height;
      
      // Background - clean white
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Top accent bar (thicker)
      ctx.fillStyle = accentColor;
      ctx.fillRect(0, 0, width, 12);
      
      // ===== HEADER SECTION WITH LARGE LOGO =====
      let yOffset = isSquare ? 50 : 80;
      
      if (logoImage) {
        // Draw LARGE logo centered at top
        const maxLogoWidth = isSquare ? 280 : 320;
        const maxLogoHeight = isSquare ? 140 : 160;
        let logoWidth = logoImage.width;
        let logoHeight = logoImage.height;
        
        // Scale down if needed, maintaining aspect ratio
        const scaleW = maxLogoWidth / logoWidth;
        const scaleH = maxLogoHeight / logoHeight;
        const scale = Math.min(scaleW, scaleH, 1);
        
        logoWidth = logoWidth * scale;
        logoHeight = logoHeight * scale;
        
        const logoX = (width - logoWidth) / 2;
        ctx.drawImage(logoImage, logoX, yOffset, logoWidth, logoHeight);
        yOffset += logoHeight + 35; // INCREASED spacing after logo
      }
      
      // Company Name (below logo)
      if (company?.nome) {
        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 36px Inter, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(company.nome.toUpperCase(), width / 2, yOffset);
        yOffset += 60; // INCREASED spacing before CONTRATA
      }
      
      // ===== "CONTRATA" BANNER =====
      const contrataText = 'CONTRATA';
      ctx.font = 'bold 72px Inter, Arial, sans-serif';
      const contrataWidth = ctx.measureText(contrataText).width;
      
      // Background rectangle for CONTRATA
      ctx.fillStyle = accentColor;
      const contrataPadding = 50;
      const contrataBoxWidth = contrataWidth + contrataPadding * 2;
      const contrataBoxHeight = 100;
      const contrataBoxX = (width - contrataBoxWidth) / 2;
      
      ctx.beginPath();
      ctx.roundRect(contrataBoxX, yOffset, contrataBoxWidth, contrataBoxHeight, 12);
      ctx.fill();
      
      // CONTRATA text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(contrataText, width / 2, yOffset + contrataBoxHeight / 2);
      ctx.textBaseline = 'alphabetic';
      
      yOffset += contrataBoxHeight + 60; // INCREASED spacing after CONTRATA
      
      // ===== JOB TITLE =====
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 52px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      
      // Word wrap job title
      const titleWords = (job?.titulo || 'Vaga').split(' ');
      let titleLines = [];
      let currentLine = '';
      
      for (const word of titleWords) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        if (ctx.measureText(testLine).width > width - 100) {
          if (currentLine) titleLines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) titleLines.push(currentLine);
      
      for (const line of titleLines) {
        ctx.fillText(line, width / 2, yOffset);
        yOffset += 62;
      }
      
      yOffset += 20;
      
      // Divider line
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(80, yOffset);
      ctx.lineTo(width - 80, yOffset);
      ctx.stroke();
      yOffset += 40;
      
      // ===== JOB DETAILS SECTION =====
      const details = [];
      
      // Calculate available space for mission text
      const footerHeight = isSquare ? 220 : 280;
      const availableHeight = height - yOffset - footerHeight;
      
      // Mission/Description - Get from JobPosition (Engenharia de Cargo) linked to this job
      // Use descricao_sumaria from the linked cargo, or fallback to job description
      const missionText = jobPosition?.descricao_sumaria || job?.descricao_sumaria;
      if (missionText) {
        // Calculate max chars based on available space (approx 40 chars per line, ~35px per line)
        const estimatedLinesAvailable = Math.floor((availableHeight - 150) / 35);
        const maxChars = Math.max(200, estimatedLinesAvailable * 45);
        const truncated = missionText.length > maxChars ? missionText.substring(0, maxChars - 3) + '...' : missionText;
        details.push({ label: 'MISSÃO DO CARGO', value: truncated, isMain: true });
      }
      
      // Benefits
      if (job?.beneficios) {
        const beneficios = Array.isArray(job.beneficios) 
          ? job.beneficios.slice(0, 4).join(' • ') 
          : job.beneficios;
        details.push({ label: 'BENEFÍCIOS', value: beneficios });
      }
      
      // Work hours
      if (job?.horario_trabalho || job?.jornada) {
        details.push({ label: 'HORÁRIO', value: job.horario_trabalho || job.jornada });
      }
      
      // Location
      if (job?.localizacao) {
        details.push({ label: 'LOCAL', value: job.localizacao });
      }
      
      // Contract type
      if (job?.tipo_contrato) {
        details.push({ label: 'CONTRATO', value: job.tipo_contrato });
      }
      
      // Draw details
      ctx.textAlign = 'left';
      const leftMargin = 80;
      const rightMargin = width - 80;
      const maxTextWidth = rightMargin - leftMargin;
      
      for (const detail of details) {
        // Check if we have space (leave room for footer)
        if (yOffset > height - footerHeight - 50) break;
        
        // Label with accent color
        ctx.fillStyle = accentColor;
        ctx.font = 'bold 24px Inter, Arial, sans-serif';
        ctx.fillText(detail.label, leftMargin, yOffset);
        yOffset += 35;
        
        // Value - word wrap with larger font
        ctx.fillStyle = '#334155';
        // Use slightly smaller font for main description if it's long
        const fontSize = detail.isMain && detail.value.length > 150 ? 24 : 28;
        ctx.font = `${fontSize}px Inter, Arial, sans-serif`;
        const lineHeight = fontSize + 6;
        
        const valueWords = detail.value.split(' ');
        let valueLine = '';
        
        for (const word of valueWords) {
          const testLine = valueLine + (valueLine ? ' ' : '') + word;
          if (ctx.measureText(testLine).width > maxTextWidth) {
            if (valueLine) {
              ctx.fillText(valueLine, leftMargin, yOffset);
              yOffset += lineHeight;
            }
            valueLine = word;
          } else {
            valueLine = testLine;
          }
        }
        if (valueLine) {
          ctx.fillText(valueLine, leftMargin, yOffset);
          yOffset += lineHeight;
        }
        
        yOffset += 20;
      }
      
      // ===== FOOTER WITH QR CODE =====
      const qrSize = isSquare ? 140 : 160;
      const footerY = height - (isSquare ? 180 : 220);
      
      // Footer background
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, footerY - 30, width, height - footerY + 30);
      
      // Bottom accent line
      ctx.fillStyle = accentColor;
      ctx.fillRect(0, height - 8, width, 8);
      
      // QR Code on the left
      const qrX = 80;
      const qrY = footerY;
      
      // QR Code white background
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20, 10);
      ctx.fill();
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw QR Code
      const qrElement = document.querySelector('[data-qr-for-banner]');
      if (qrElement) {
        const svgData = new XMLSerializer().serializeToString(qrElement);
        const qrImg = new Image();
        await new Promise((resolve) => {
          qrImg.onload = resolve;
          qrImg.onerror = resolve;
          qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        });
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      }
      
      // Text next to QR Code
      const textX = qrX + qrSize + 40;
      ctx.textAlign = 'left';
      
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 28px Inter, Arial, sans-serif';
      ctx.fillText('Candidate-se pelo App!', textX, qrY + 40);
      
      ctx.fillStyle = '#64748b';
      ctx.font = '22px Inter, Arial, sans-serif';
      ctx.fillText('Escaneie o QR Code para', textX, qrY + 75);
      ctx.fillText('baixar o aplicativo', textX, qrY + 102);
      
      // Website alternative
      ctx.fillStyle = '#475569';
      ctx.font = '20px Inter, Arial, sans-serif';
      ctx.fillText('ou pelo site: www.g2egente.com.br', textX, qrY + 132);
      
      // G2eGente branding
      ctx.fillStyle = accentColor;
      ctx.font = 'bold 26px Inter, Arial, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('G2eGente', width - 80, qrY + qrSize - 10);
      
      // Convert to image and download
      const link = document.createElement('a');
      link.download = `vaga-${job?.titulo?.substring(0,20).replace(/\s/g, '-') || job?.id?.substring(0,8)}-${format}.png`;
      link.href = canvas.toDataURL('image/png', 0.95);
      link.click();
      
      toast.success('Banner gerado com sucesso!');
    } catch (error) {
      console.error('Error generating banner:', error);
      toast.error('Erro ao gerar banner');
    } finally {
      setGenerating(false);
    }
  };

  const openInNewTab = () => {
    window.open(jobUrl, '_blank');
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-cyan-600" />
            Compartilhar Vaga
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Job Info */}
          <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
            <p className="font-semibold text-cyan-900">{job.titulo}</p>
            <p className="text-sm text-cyan-700">{job.localizacao}</p>
          </div>

          {/* Banner Generation Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-700">
              Gerar Banner para Redes Sociais
            </label>
            
            {/* Format Selection */}
            <div className="flex gap-2">
              <Button
                variant={format === 'square' ? 'default' : 'outline'}
                onClick={() => setFormat('square')}
                className={format === 'square' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
              >
                <Square className="w-4 h-4 mr-2" />
                Feed (1080x1080)
              </Button>
              <Button
                variant={format === 'vertical' ? 'default' : 'outline'}
                onClick={() => setFormat('vertical')}
                className={format === 'vertical' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Stories (1080x1920)
              </Button>
            </div>
            
            {/* Generate Button */}
            <Button 
              onClick={generateBanner}
              disabled={generating}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Banner {format === 'square' ? 'Quadrado' : 'Vertical'}
                </>
              )}
            </Button>
            
            {!company?.logo_url && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                Dica: Adicione a logo da sua empresa em Configurações para personalizar o banner!
              </p>
            )}
          </div>

          {/* Hidden QR Code for banner generation */}
          <div style={{ position: 'absolute', left: '-9999px' }}>
            <QRCodeSVG 
              data-qr-for-banner
              value={PWA_URL}
              size={200}
              level="H"
              fgColor="#0891b2"
            />
          </div>

          {/* Link Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Link da Vaga</label>
            <div className="flex gap-2">
              <Input 
                value={jobUrl}
                readOnly
                className="text-sm bg-slate-50"
              />
              <Button 
                onClick={copyToClipboard}
                className="bg-cyan-600 hover:bg-cyan-700 flex-shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3">
            <Button 
              onClick={openInNewTab}
              variant="outline"
              className="flex-1 border-cyan-400 text-cyan-700 hover:bg-cyan-50"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Visualizar Vaga
            </Button>
          </div>

          {/* Tips */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-700">
              <strong>O banner inclui:</strong>
            </p>
            <ul className="text-xs text-slate-600 mt-1 space-y-1 ml-4 list-disc">
              <li>Logo grande da empresa no cabeçalho</li>
              <li>Nome da empresa em destaque</li>
              <li>"CONTRATA" em banner chamativo</li>
              <li>Missão e detalhes da vaga</li>
              <li>QR Code para baixar o aplicativo</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
